import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import accountingService from '../../services/accountingService';
import realDataAccountingService from '../../services/realDataAccountingService';
import backendAccountingService from '../../services/backendAccountingService';
import {
  setDashboardData,
  setReportLoading,
  setReportError,
  setPrintData
} from '../../redux/accounting';

const AccountingDashboard = () => {
  const dispatch = useDispatch();
  const { dashboardData, reportsLoading, reportsError } = useSelector(state => state.accounting);
  const { admin } = useSelector(state => state.admin);
  const { schooldetails } = useSelector(state => state.schooldetails);
  
  // Get currency from school details (ensure it's always a valid string)
  const currency = (schooldetails && schooldetails.length > 0 && schooldetails[0].currency) 
    ? String(schooldetails[0].currency).toUpperCase()
    : (schooldetails && schooldetails.length > 0 && schooldetails[0].country === 'LR' ? 'USD' : 'GHS');
  
  const getCurrencySymbol = () => {
    return currency === 'GHS' ? '₵' : currency === 'USD' ? '$' : (currency || '₵');
  };
  
  const formatCurrency = (amount) => {
    return accountingService.formatCurrency(amount, currency || 'GHS');
  };
  
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const currentSchool = schooldetails && schooldetails.length > 0 ? schooldetails[0] : null;

  useEffect(() => {
    if (currentSchool?.schoolID) {
      loadDashboardData();
    }
  }, [currentSchool]);

  // Get Redux data at component level - using correct structure
  const reduxData = {
    students: useSelector(state => state.students) || [],
    teachers: useSelector(state => state.teachers) || [],
    expenses: useSelector(state => state.expenses) || [],
    sales: useSelector(state => state.sales?.sales) || [],
    assets: useSelector(state => state.assets?.assets) || [],
    class: useSelector(state => state.class) || []
  };

  // Debug logging for Redux data
  console.log('🔍 AccountingDashboard Redux data:', {
    students: reduxData.students?.length || 0,
    teachers: reduxData.teachers?.length || 0,
    expenses: reduxData.expenses?.length || 0,
    sales: reduxData.sales?.length || 0,
    assets: reduxData.assets?.length || 0,
    class: reduxData.class?.length || 0,
    studentsSample: reduxData.students?.slice(0, 1),
    salesSample: reduxData.sales?.slice(0, 1),
    expensesSample: reduxData.expenses?.slice(0, 1),
    teachersSample: reduxData.teachers?.slice(0, 1),
    classSample: reduxData.class?.slice(0, 1)
  });

  // Check if we have any financial data
  const hasStudents = reduxData.students?.length > 0;
  const hasClassFees = reduxData.class?.length > 0;
  const hasSales = reduxData.sales?.length > 0;
  const hasExpenses = reduxData.expenses?.length > 0;

  console.log('📊 Data availability check:', {
    hasStudents,
    hasClassFees,
    hasSales,
    hasExpenses,
    currentSchool: currentSchool?.schoolID
  });

  // Check if we have any data at all
  const hasAnyData = reduxData.students?.length > 0 || 
                     reduxData.teachers?.length > 0 || 
                     reduxData.expenses?.length > 0 || 
                     reduxData.sales?.length > 0 || 
                     reduxData.assets?.length > 0;

  console.log('Has any data:', hasAnyData);

  const loadDashboardData = async () => {
    if (!currentSchool?.schoolID) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading dashboard data from Redux for school:', currentSchool.schoolID);
      console.log('📊 Redux data available:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0,
        class: reduxData.class?.length || 0
      });
      
      // Get dashboard data from Redux data
      const data = await realDataAccountingService.getRealDashboardData(
        currentSchool?.schoolID,
        reduxData
      );
      
      console.log('📈 Redux dashboard data received:', data);
      console.log('💰 Revenue breakdown:', {
        totalRevenue: data?.keyMetrics?.totalRevenue,
        studentFinance: data?.studentFinance?.paidFees,
        canteenFees: data?.canteenFees?.totalRevenue,
        sales: data?.sales?.totalRevenue
      });
      dispatch(setDashboardData(data));
    } catch (err) {
      setError('Failed to load dashboard data from Redux');
      console.error('Redux dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handlePrint = (reportType) => {
    dispatch(setPrintData({ reportType, data: dashboardData }));
    // Print logic would be implemented here
  };

  const handleExport = async (reportType, format = 'pdf') => {
    try {
      const data = await accountingService.exportReport(reportType, dashboardData, format);
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const keyMetrics = [
    {
      title: 'Total Revenue',
      value: `${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.totalRevenue || 0).toLocaleString()}`,
      icon: <TrendingUpIcon />,
      color: '#1976d2',
      trend: '+12%',
      details: [
        `School Fees: ${getCurrencySymbol()} ${(dashboardData?.studentFinance?.paidFees || 0).toLocaleString()}`,
        `Canteen: ${getCurrencySymbol()} ${(dashboardData?.canteenFees?.totalRevenue || 0).toLocaleString()}`,
        `Sales: ${getCurrencySymbol()} ${(dashboardData?.sales?.totalRevenue || 0).toLocaleString()}`
      ]
    },
    {
      title: 'Total Expenses',
      value: `${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.totalExpenses || 0).toLocaleString()}`,
      icon: <AccountBalanceIcon />,
      color: '#d32f2f',
      trend: '+8%',
      details: [
        `Salaries: ${getCurrencySymbol()} ${(dashboardData?.salaries?.totalSalaries || 0).toLocaleString()}`,
        `Other: ${getCurrencySymbol()} ${(dashboardData?.expenditure?.totalExpenses || 0).toLocaleString()}`
      ]
    },
    {
      title: 'Net Income',
      value: `${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.netIncome || 0).toLocaleString()}`,
      icon: <AssessmentIcon />,
      color: dashboardData?.keyMetrics?.netIncome >= 0 ? '#388e3c' : '#d32f2f',
      trend: dashboardData?.keyMetrics?.netIncome >= 0 ? '+15%' : '-5%',
      details: [
        dashboardData?.keyMetrics?.netIncome >= 0 ? 'Profit' : 'Loss',
        `Revenue: ${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.totalRevenue || 0).toLocaleString()}`,
        `Expenses: ${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.totalExpenses || 0).toLocaleString()}`
      ]
    },
    {
      title: 'Cash Balance',
      value: `${getCurrencySymbol()} ${(dashboardData?.keyMetrics?.cashBalance || 0).toLocaleString()}`,
      icon: <CheckCircleIcon />,
      color: '#f57c00',
      trend: '+3%',
      details: [
        `Base: ${getCurrencySymbol()} 50,000`,
        `Net Change: ${getCurrencySymbol()} ${((dashboardData?.keyMetrics?.cashBalance || 0) - 50000).toLocaleString()}`
      ]
    }
  ];

  const quickReports = [
    {
      title: 'Trial Balance',
      description: 'Current period trial balance',
      icon: <AssessmentIcon />,
      color: '#1976d2',
      reportType: 'trial-balance'
    },
    {
      title: 'Balance Sheet',
      description: 'Financial position statement',
      icon: <AccountBalanceIcon />,
      color: '#388e3c',
      reportType: 'balance-sheet'
    },
    {
      title: 'Income Statement',
      description: 'Profit and loss statement',
      icon: <TrendingUpIcon />,
      color: '#f57c00',
      reportType: 'income-statement'
    },
    {
      title: 'Cash Flow',
      description: 'Cash flow statement',
      icon: <CheckCircleIcon />,
      color: '#9c27b0',
      reportType: 'cash-flow'
    }
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading dashboard...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Accounting Dashboard
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Data Summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Data Summary</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Students</Typography>
            <Typography variant="h6">{reduxData.students?.length || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Teachers</Typography>
            <Typography variant="h6">{reduxData.teachers?.length || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Expenses</Typography>
            <Typography variant="h6">{reduxData.expenses?.length || 0}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" color="text.secondary">Sales</Typography>
            <Typography variant="h6">{reduxData.sales?.length || 0}</Typography>
          </Grid>
        </Grid>
        {!hasAnyData && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            No financial data found. Please ensure you have students, teachers, expenses, or sales data in your system.
          </Alert>
        )}
        {dashboardData?.lastUpdated && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Last updated: {new Date(dashboardData.lastUpdated).toLocaleString()}
          </Typography>
        )}
      </Paper>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {keyMetrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ color: metric.color, mr: 1 }}>
                    {metric.icon}
                  </Box>
                  <Typography variant="h6" component="div">
                    {metric.title}
                  </Typography>
                </Box>
                <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                  {metric.value}
                </Typography>
                {metric.details && (
                  <Box sx={{ mb: 1 }}>
                    {metric.details.map((detail, idx) => (
                      <Typography key={idx} variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {detail}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Chip 
                  label={metric.trend} 
                  size="small" 
                  color={metric.trend.startsWith('+') ? 'success' : 'error'}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Reports */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label="Financial Reports" />
            <Tab label="Overdue & Defaulters" />
            <Tab label="Analytics & Forecasting" />
            <Tab label="Audit & Compliance" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3 }}>
          {selectedTab === 0 && (
            <Grid container spacing={3}>
              {quickReports.map((report, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: report.color, mr: 1 }}>
                          {report.icon}
                        </Box>
                        <Typography variant="h6" component="div">
                          {report.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {report.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        startIcon={<PrintIcon />}
                        onClick={() => handlePrint(report.reportType)}
                      >
                        Print
                      </Button>
                      <Button 
                        size="small" 
                        startIcon={<DownloadIcon />}
                        onClick={() => handleExport(report.reportType)}
                      >
                        Export
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          
          {selectedTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Overdue & Defaulters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue fees and defaulters management features will be implemented here.
              </Typography>
            </Box>
          )}
          
          {selectedTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Analytics & Forecasting
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Financial forecasting and analytics features will be implemented here.
              </Typography>
            </Box>
          )}
          
          {selectedTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Audit & Compliance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Audit trail and compliance features will be implemented here.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Alerts */}
      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Alerts
          </Typography>
          {dashboardData.alerts.map((alert, index) => (
            <Alert key={index} severity={alert.severity} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default AccountingDashboard;
